import { useEffect, useTransition } from "react";

import { SteppedMultiColumnDropdown } from "@stacklet/ui";
import { graphql, usePaginationFragment } from "react-relay";
import { useDebouncedCallback } from "use-debounce";

import { useSteppedMultiColumnDropdown } from "../../hooks/useSteppedMultiColumnDropdown";
import { FilterCriteriaCard } from "../FilterCriteriaCard";
import {
  createPathAwareDropdown,
  pathOperators,
  column,
  OPERATORS,
  VALUES,
  conditions,
} from "./DefinitionBuilder";
import { useProviderFilterCombiner } from "./hooks";

import type { ResourceCriteria_resourceTypes$key } from "./__generated__/ResourceCriteria_resourceTypes.graphql";
import type { ResourceCriteria_tagKeys$key } from "./__generated__/ResourceCriteria_tagKeys.graphql";
import type { ResourceCriteria_tagValues$key } from "./__generated__/ResourceCriteria_tagValues.graphql";
import type { CriteriaFilterValue } from "@stacklet/ui";

interface Props {
  refs: {
    tagKeysRef: ResourceCriteria_tagKeys$key;
    tagValuesRef: ResourceCriteria_tagValues$key;
    resourceTypesRef: ResourceCriteria_resourceTypes$key;
  };
  handleSelectionsChange?: (selections: CriteriaFilterValue[]) => void;
  initialCriteriaFilters?: CriteriaFilterValue[];
}

const DEFAULT_SELECTIONS = ["resource-tag", "", "is-one-of"];

export function ResourceCriteria({
  handleSelectionsChange,
  refs,
  initialCriteriaFilters,
}: Props) {
  const [, startTransition] = useTransition();

  const { combineFilters } = useProviderFilterCombiner({
    fieldName: "qualified-name",
    operator: "contains",
  });

  const {
    data: tagKeysData,
    loadNext: loadNextTagKeys,
    hasNext: hasNextTagKeys,
    refetch: refetchTagKeys,
  } = usePaginationFragment(
    graphql`
      fragment ResourceCriteria_tagKeys on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 100 }
        cursor: { type: "String" }
        filter: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "ResourceCriteriaTagKeysPaginationQuery") {
        resourceCriteriaTagKeys: resourceTagKeys(
          first: $count
          after: $cursor
          filterElement: $filter
        ) @connection(key: "ResourceCriteria__resourceCriteriaTagKeys") {
          edges {
            node {
              key
            }
          }
        }
      }
    `,
    refs.tagKeysRef,
  );

  const {
    data: tagValuesData,
    loadNext: loadNextTagValues,
    hasNext: hasNextTagValues,
    refetch: refetchTagValues,
  } = usePaginationFragment(
    graphql`
      fragment ResourceCriteria_tagValues on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 100 }
        cursor: { type: "String" }
        filter: { type: "FilterElementInput", defaultValue: null }
      )
      @refetchable(queryName: "ResourceCriteriaTagValuesPaginationQuery") {
        resourceCriteriaTagValues: resourceTagValues(
          first: $count
          after: $cursor
          filterElement: $filter
        ) @connection(key: "ResourceCriteria__resourceCriteriaTagValues") {
          edges {
            node {
              value
            }
          }
        }
      }
    `,
    refs.tagValuesRef,
  );

  const {
    data: resourceTypesData,
    loadNext: loadNextResourceTypes,
    hasNext: hasNextResourceTypes,
    refetch: refetchResourceTypes,
  } = usePaginationFragment(
    graphql`
      fragment ResourceCriteria_resourceTypes on Query
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 100 }
        cursor: { type: "String" }
        filter: { type: "FilterElementInput" }
      )
      @refetchable(queryName: "ResourceCriteriaResourceTypesPaginationQuery") {
        resourceCriteriaTypes: resourceTypes(
          first: $count
          after: $cursor
          filterElement: $filter
        ) @connection(key: "ResourceCriteria__resourceCriteriaTypes") {
          edges {
            node {
              qualifiedName
            }
          }
        }
      }
    `,
    refs.resourceTypesRef,
  );

  // Transform tag keys data for the dropdown (Relay automatically merges edges with @connection)
  const tagKeyItems =
    tagKeysData.resourceCriteriaTagKeys?.edges.map((edge) => ({
      id: edge.node.key,
      name: edge.node.key,
    })) ?? [];

  // Transform tag values data for the dropdown (Relay automatically merges edges with @connection)
  const tagValueItems =
    tagValuesData.resourceCriteriaTagValues?.edges.map((edge) => ({
      id: edge.node.value,
      name: edge.node.value,
    })) ?? [];

  // Transform resource types data for the dropdown (Relay automatically merges edges with @connection)
  const resourceTypeItems =
    resourceTypesData.resourceCriteriaTypes?.edges.map((edge) => ({
      id: edge.node.qualifiedName,
      name: edge.node.qualifiedName,
    })) ?? [];

  // Refetch data when providers change
  useEffect(() => {
    startTransition(() => {
      const filter = combineFilters(null);
      refetchResourceTypes({ filter });
      // Tag keys are not provider-scoped; refetch without provider filter
      refetchTagKeys({});
    });
  }, [combineFilters, refetchResourceTypes, refetchTagKeys]);

  // Configuration
  const config = createPathAwareDropdown()
    .criteria([
      { id: "resource-tag", name: "Resource Tag" },
      { id: "resource-type", name: "Resource Type" },
    ])
    // Resource tag path
    .path("resource-tag", (path) =>
      path
        .column(
          column
            .search("Select Tag Key", tagKeyItems)
            .withPlaceholder("Search or enter value")
            .allowCustomValue(),
        )
        .column(pathOperators(OPERATORS.STRING_EXTENDED))
        .column(
          // Special existence values
          column
            .list("Select Value", VALUES.EXISTENCE)
            .withCondition(conditions.includesAny(2, "is", "is-not")),
          // Search for equals/does-not-equal
          column
            .search("Select Value", tagValueItems)
            .withCondition(
              conditions.includesAny(2, "equals", "does-not-equal"),
            )
            .withPlaceholder("Search or enter value")
            .allowCustomValue(),
          // Search for contains and other text operators
          column
            .search("Select Value", tagValueItems)
            .withCondition(
              conditions.includesAny(
                2,
                "contains",
                "does-not-contain",
                "starts-with",
                "ends-with",
              ),
            )
            .withPlaceholder("Search or enter value")
            .allowCustomValue(),
        ),
    )
    // Resource type path
    .path("resource-type", (path) =>
      path.column(pathOperators(OPERATORS.EQUALITY_AND_SELECTION)).column(
        // Checkbox for multi-select operators
        column
          .checkbox("Select Resource Type", resourceTypeItems)
          .withCondition(
            conditions.includesAny(1, "is-one-of", "is-not-one-of"),
          )
          .withPlaceholder("Search Value"),
        // Search for equals/does-not-equal
        column
          .search("Select Value", resourceTypeItems)
          .withCondition(conditions.includesAny(1, "equals", "does-not-equal"))
          .withPlaceholder("Search value")
          .allowCustomValue(),
      ),
    )
    .build();

  const handleSearch = useDebouncedCallback(
    (searchTerm: string, columnIndex: number, selections?: string[]) => {
      const value = searchTerm.trim();
      const activeCriteria = selections?.[0];

      startTransition(() => {
        // Determine which fragment to refetch based on active criteria and column
        // columnIndex includes criteria column: 0=criteria, 1=tag key, 2=operator, 3=tag value
        if (activeCriteria === "resource-tag") {
          if (columnIndex === 1) {
            // Tag keys are not provider-scoped; search without provider filter
            const searchFilter = value
              ? { single: { name: "key", value } }
              : null;
            refetchTagKeys({
              filter: searchFilter,
            });
          } else if (columnIndex === 3) {
            // Tag values - always pass value (even empty string) to maintain tag key context
            refetchTagValues({
              filter: { single: { name: "value", value } },
            });
          }
        } else if (activeCriteria === "resource-type") {
          // Resource types - columnIndex 2 is the value column for resource-type
          if (columnIndex === 2) {
            const searchFilter = value
              ? { single: { name: "qualified-name", value } }
              : null;
            refetchResourceTypes({
              filter: combineFilters(searchFilter),
            });
          }
        }
      });
    },
    300,
  );

  const handleScrollEnd = (columnIndex: number, selections?: string[]) => {
    const activeCriteria = selections?.[0];

    if (activeCriteria === "resource-tag") {
      // columnIndex includes criteria column: 0=criteria, 1=tag key, 2=operator, 3=tag value
      if (columnIndex === 1 && hasNextTagKeys) {
        loadNextTagKeys(100);
      } else if (columnIndex === 3 && hasNextTagValues) {
        loadNextTagValues(100);
      }
    } else if (activeCriteria === "resource-type") {
      // columnIndex includes criteria column: 0=criteria, 1=operator, 2=resource type value
      if (columnIndex === 2 && hasNextResourceTypes) {
        loadNextResourceTypes(100);
      }
    }
  };

  const { criteriaFilters, handleChange, handleRemove, initialSelections } =
    useSteppedMultiColumnDropdown({
      handleSelectionsChange,
      initialCriteriaFilters,
      defaultSelections: DEFAULT_SELECTIONS,
    });

  return (
    <FilterCriteriaCard
      helperTextOptions={["Resource Tag", "Resource Type"]}
      onRemoveCriterion={handleRemove}
      selectedCriteria={criteriaFilters}
      title="Resource"
    >
      <SteppedMultiColumnDropdown
        config={config}
        initialSelections={initialSelections}
        onChangeStructured={handleChange}
        onScrollEnd={handleScrollEnd}
        onSearch={handleSearch}
      />
    </FilterCriteriaCard>
  );
}
